import { useState, useEffect } from 'react';
import { getDeviceData, getDeviceDailyMetrics } from '../utils/api';

// mode: 'raw' (measurements over the requested range) or 'daily' (daily averages
// and daily light integrals over the last year). Note light_value means
// different things per mode: lux for 'raw', lux·h/day (DLI) for 'daily' — only
// the latter is comparable to the plant profile's light bands.
//
// range is one of the keys in HISTORY_RANGES and applies to 'raw' only; 'daily'
// has a fixed year-long window. Beyond 7 days the server buckets the samples,
// and light becomes the brightest reading in each bucket rather than the mean,
// so the Full Shade..Direct Light scale keeps describing the light the plant
// actually sits in instead of being dragged down by the nights.
//
// firstReadingAt is the device's oldest sample, or null when it has never
// reported. It does not depend on the range, so a range selector driven by it
// stays stable while the user switches between ranges.
export const useChartData = (deviceId, mode = 'raw', range = undefined) => {
	const [chartData, setChartData] = useState({});
	const [firstReadingAt, setFirstReadingAt] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// firstReadingAt describes the device, not the window, so it deliberately
	// survives a range switch: clearing it there would grey out the other ranges
	// for as long as the new one takes to load.
	useEffect(() => {
		setFirstReadingAt(null);
	}, [deviceId]);

	useEffect(() => {
		const fetchChartData = async () => {
			if (!deviceId) {
				setLoading(false);
				return;
			}

			// Reset per device, otherwise switching devices renders the
			// previous device's series with loading already false.
			setLoading(true);
			setError(null);
			setChartData({});

			try {
				const response = mode === 'daily' ? await getDeviceDailyMetrics(deviceId) : await getDeviceData(deviceId, range);
				if (!response.ok) {
					throw new Error('Failed to fetch chart data');
				}
				const json = await response.json();
				const data = json.data;
				// Absent in 'daily' mode, which offers no range to unlock.
				setFirstReadingAt(json.first_reading_at || null);

				if (data === null) {
					setLoading(false);
					return;
				}

				const temp_values = data.map(d => [new Date(d.datetime).getTime(), d.temp_value]);
				const moist_values = data.map(d => [new Date(d.datetime).getTime(), d.moist_value]);
				const light_values = data.map(d => [new Date(d.datetime).getTime(), d.light_value]);
				const nutrition_values = data.map(d => [new Date(d.datetime).getTime(), d.nutrition_value]);
				// The API marks a missing reading with a value that cannot occur
				// naturally: -1 for battery (0% is a real, flat cell) and >= 0 for
				// rssi (0 dBm is physically unreachable). Map those to null so
				// ECharts draws a gap. Timestamps stay aligned across every series,
				// which tooltip trigger: 'axis' relies on.
				const battery_values = data.map(d => [new Date(d.datetime).getTime(), d.battery_value < 0 ? null : d.battery_value]);
				const rssi_values = data.map(d => [new Date(d.datetime).getTime(), d.rssi_value >= 0 ? null : d.rssi_value]);

				setChartData({
					temp_values,
					moist_values,
					light_values,
					nutrition_values,
					battery_values,
					rssi_values
				});
				setError(null);
			} catch (err) {
				setError(err.message);
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		fetchChartData();
	}, [deviceId, mode, range]);

	return { chartData, firstReadingAt, loading, error };
};

export default useChartData;
